/**
 * External Imports
 */
import {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useMemo,
  useState
} from 'react';
import { useDispatch } from 'react-redux';
import { format } from 'date-fns';

/**
 * Nexus Imports
 */
import { useAnalyticsApi } from '@shared-web-analytics/react/dist';

/**
 * Internal Imports
 */
import RoundDateTimeSelect from './RoundDateTimeSelect';
import RoundTemplateSelect from './RoundTemplateSelect';
import StartNewRoundButton from './StartNewRoundButton';
import CloseBtn from '../../shared/buttons/CloseBtn';
import { setRoundStarted } from '../../../redux/actions';
import { logRoundClick } from '../../../utils/analyticsHelpers';
import { useDebounceValue } from '../../../utils/debouncers';
import { date } from '../../../utils/helpers';
import supportRoundingTypes from '../../../constants/supportRoundingTypes.json';

/**
 * Global Type Definition Imports
 */
import { ICondensedMasterTemplate } from '../../../config/interfaces';

/**
 * Style Imports
 */
import {
  CloseBtnContainer,
  RoundCloseContainer,
  StartNeuButtonContainer
} from './Header.styles';

interface RoundHeaderProps {
  day: string;
  hideCloseButton?: boolean;
  isNew: boolean;
  isRoundStarted: boolean;
  isViewOnly: boolean;
  masterTemplateOptions: ICondensedMasterTemplate[];
  roundBtnEnabled?: boolean;
  selectedTemplate?: ICondensedMasterTemplate;
  selectedDateTime: string;
  selectedTime: string;
  type: string;
  handleCloseClick: () => void;
  handleDateTimeChange: (e: any) => void;
  handleTemplateSelect: (template: ICondensedMasterTemplate) => void;
  setDay: Dispatch<SetStateAction<string>>;
}

const RoundHeader: FC<RoundHeaderProps> = ({
  day,
  hideCloseButton,
  isNew,
  isRoundStarted,
  isViewOnly,
  masterTemplateOptions,
  roundBtnEnabled,
  selectedTemplate,
  selectedDateTime,
  selectedTime,
  type,
  handleCloseClick,
  handleDateTimeChange,
  handleTemplateSelect,
  setDay
}) => {
  const dispatch = useDispatch();

  const [open, setOpen] = useState(false);
  const [activeSearch, setActiveSearch] = useState<string>('');
  const [query, setQuery] = useState('');

  const debouncedQuery = useDebounceValue(query, 250);

  const { logTrackingEvent } = useAnalyticsApi();

  const handleStartNewRoundClick = () => {
    logTrackingEvent(
      logRoundClick(
        'start',
        supportRoundingTypes.validation.includes(type) ||
          supportRoundingTypes.support.includes(type)
          ? type
          : undefined
      )
    );
    dispatch(setRoundStarted());
  };

  const handleTypeAheadChange = useCallback(
    (e: any) => setQuery(e.target.value),
    []
  );

  const isInvalidTime = useMemo(
    () => day === 'today' && format(new Date(), 'HH:mm') < selectedTime,
    [selectedTime, day]
  );
  const isInvalidDateTime = useMemo(
    () => selectedDateTime > date.convertToDateTime(new Date()),
    [selectedDateTime]
  );

  const searchResults = useMemo(() => {
    switch (activeSearch) {
      case 'round-template-select':
        return masterTemplateOptions.filter(
          (template: ICondensedMasterTemplate) =>
            template.name
              .toLowerCase()
              .includes(debouncedQuery.trim().toLowerCase())
        );
      default:
        return undefined;
    }
  }, [activeSearch, masterTemplateOptions, debouncedQuery]);

  return (
    <section
      className="position-relative"
      style={{ minHeight: '128px', overflowY: 'visible' }}
    >
      <RoundCloseContainer className="d-flex justify-content-center">
        {!hideCloseButton && (
          <>
            <CloseBtnContainer className="ps-3">
              <CloseBtn
                text={
                  supportRoundingTypes.validation.includes(type)
                    ? 'Validation'
                    : 'Round'
                }
                handleCloseClick={handleCloseClick}
              />
            </CloseBtnContainer>
            <div className="d-flex flex-row">
              <p style={{ fontStyle: 'italic', marginLeft: '5px' }}>
                You&apos;re viewing a previous round.
              </p>
            </div>
          </>
        )}
      </RoundCloseContainer>
      <div
        className="d-flex flex-row align-items-center pb-2 ps-5"
        style={{ gap: '0.5rem' }}
      >
        <RoundTemplateSelect
          closeOnClick
          disabled={
            !isNew ||
            isRoundStarted ||
            supportRoundingTypes.support.includes(type)
          }
          disabledWithTransparent={
            isNew && supportRoundingTypes.support.includes(type)
          }
          focused={activeSearch === 'round-template-select'}
          name="round-template-select"
          open={open}
          options={masterTemplateOptions}
          placeholder={selectedTemplate?.name || ''}
          query={query}
          searchResults={searchResults}
          styles={{ paddingTop: '3px' }}
          selectedTemplate={selectedTemplate}
          typeAhead
          handleTemplateSelect={handleTemplateSelect}
          setActiveSearch={setActiveSearch}
          setOpen={setOpen}
          setQuery={setQuery}
          setTypeAhead={setActiveSearch}
          setTypeAheadChange={handleTypeAheadChange}
        />
        <RoundDateTimeSelect
          day={day}
          isViewOnly={isViewOnly}
          selectedTime={selectedTime}
          selectedDateTime={selectedDateTime}
          type={type}
          handleDateTimeChange={handleDateTimeChange}
          setDay={setDay}
        />
      </div>
      {!isRoundStarted && isNew && (
        <StartNeuButtonContainer>
          <StartNewRoundButton
            disabled={isInvalidTime || isInvalidDateTime || !roundBtnEnabled}
            handleStartNewRoundClick={handleStartNewRoundClick}
            type={type}
          />
        </StartNeuButtonContainer>
      )}
    </section>
  );
};

export default RoundHeader;
